import { msg } from 'gt-react';

/**
 * Present-progressive labels for the assistant's server action tools, shown
 * on the tool-call rows while a call runs (the backend sends no
 * `userFacingName` for harness tools). Decoded with `useMessages()`.
 */
export const NAMI_TOOL_LABELS = {
  update_line_price: msg('Updating line price…', {
    context: 'AI assistant tool label',
  }),
  update_line_quantity: msg('Updating line quantity…', {
    context: 'AI assistant tool label',
  }),
  update_line_fields: msg('Updating line details…', {
    context: 'AI assistant tool label',
  }),
  match_line_product: msg('Matching line to product…', {
    context: 'AI assistant tool label',
  }),
  add_line: msg('Adding a line…', { context: 'AI assistant tool label' }),
  remove_line: msg('Previewing line removal…', {
    context: 'AI assistant tool label',
  }),
  update_header_fields: msg('Updating order details…', {
    context: 'AI assistant tool label',
  }),
  adjust_line_prices: msg('Previewing price changes…', {
    context: 'AI assistant tool label',
  }),
  apply_line_discounts: msg('Applying discounts…', {
    context: 'AI assistant tool label',
  }),
  remove_line_discounts: msg('Removing discounts…', {
    context: 'AI assistant tool label',
  }),
  create_bulk_discount_group: msg('Previewing a discount group…', {
    context: 'AI assistant tool label',
  }),
  approve_order: msg('Previewing approval…', {
    context: 'AI assistant tool label',
  }),
  reject_order: msg('Previewing rejection…', {
    context: 'AI assistant tool label',
  }),
  resolve_mismatch: msg('Resolving mismatch…', {
    context: 'AI assistant tool label',
  }),
  retry_erp_submission: msg('Previewing ERP retry…', {
    context: 'AI assistant tool label',
  }),
  poll_erp_order_number: msg('Checking the ERP…', {
    context: 'AI assistant tool label',
  }),
  reassign_order: msg('Previewing reassignment…', {
    context: 'AI assistant tool label',
  }),
  delete_order: msg('Previewing deletion…', {
    context: 'AI assistant tool label',
  }),
  create_discount_group: msg('Previewing a discount group…', {
    context: 'AI assistant tool label',
  }),
  update_discount_group: msg('Updating discount group…', {
    context: 'AI assistant tool label',
  }),
  add_discount_element: msg('Adding a discount element…', {
    context: 'AI assistant tool label',
  }),
  update_discount_element: msg('Updating a discount element…', {
    context: 'AI assistant tool label',
  }),
  delete_discount_element: msg('Previewing element removal…', {
    context: 'AI assistant tool label',
  }),
  deactivate_discount_element: msg('Previewing element deactivation…', {
    context: 'AI assistant tool label',
  }),
  submit_discount_group_for_approval: msg('Submitting for approval…', {
    context: 'AI assistant tool label',
  }),
  decide_discount_group: msg('Previewing the decision…', {
    context: 'AI assistant tool label',
  }),
  revert_discount_group_to_draft: msg('Previewing revert to draft…', {
    context: 'AI assistant tool label',
  }),
  deactivate_discount_group: msg('Previewing deactivation…', {
    context: 'AI assistant tool label',
  }),
  delete_discount_group: msg('Previewing deletion…', {
    context: 'AI assistant tool label',
  }),
  resolve_discount_unresolved_line: msg('Resolving a discount line…', {
    context: 'AI assistant tool label',
  }),
  dismiss_discount_unresolved_line: msg('Dismissing a discount line…', {
    context: 'AI assistant tool label',
  }),
  set_customer_price_list: msg('Setting the customer price list…', {
    context: 'AI assistant tool label',
  }),
  activate_price_list_overlay: msg('Previewing activation…', {
    context: 'AI assistant tool label',
  }),
  deactivate_price_list_overlay: msg('Previewing deactivation…', {
    context: 'AI assistant tool label',
  }),
  rematch_price_list_overlay: msg('Rematching the price list…', {
    context: 'AI assistant tool label',
  }),
  create_customer_product_match: msg('Creating a product match…', {
    context: 'AI assistant tool label',
  }),
  update_customer_product_match: msg('Updating a product match…', {
    context: 'AI assistant tool label',
  }),
  delete_customer_product_match: msg('Previewing match removal…', {
    context: 'AI assistant tool label',
  }),
  list_sales_orders: msg('Listing sales orders…', {
    context: 'AI assistant tool label',
  }),
  get_sales_order: msg('Reading the order…', {
    context: 'AI assistant tool label',
  }),
  search_customers: msg('Searching customers…', {
    context: 'AI assistant tool label',
  }),
  sales_order_metrics: msg('Computing order metrics…', {
    context: 'AI assistant tool label',
  }),
  discount_metrics: msg('Computing discount metrics…', {
    context: 'AI assistant tool label',
  }),
  export_sales_orders_csv: msg('Exporting sales orders…', {
    context: 'AI assistant tool label',
  }),
  export_discounts_csv: msg('Exporting discounts…', {
    context: 'AI assistant tool label',
  }),
  search_products: msg('Searching products…', {
    context: 'AI assistant tool label',
  }),
  product_pricing: msg('Looking up prices…', {
    context: 'AI assistant tool label',
  }),
  product_margins: msg('Looking up margins…', {
    context: 'AI assistant tool label',
  }),
} as const;

export type NamiToolName = keyof typeof NAMI_TOOL_LABELS;

export function isNamiToolName(name: string): name is NamiToolName {
  return Object.hasOwn(NAMI_TOOL_LABELS, name);
}
